import { useEffect, useRef, useState } from 'react';

import { useDebounce } from '@/hooks/useDebounce';

const TYPING_PAUSE_MS = 300;

/**
 * A text the reader types into, shown as typed and handed to `commit` once they pause: a filter kept
 * in the address is written once per pause rather than once per key. When `value` changes from
 * elsewhere -- a navigation that clears the address -- the field follows it.
 */
export const useDebouncedCommit = (value: string, commit: (text: string) => void) => {
    const [draft, setDraft] = useState(value);
    const [committed, setCommitted] = useState(value);
    const pausedDraft = useDebounce(draft, TYPING_PAUSE_MS);
    // Read through a ref: a caller writing it inline hands a new function at every render.
    const commitRef = useRef(commit);

    // Declared before the effect that commits, so it always calls the latest `commit`.
    useEffect(() => {
        commitRef.current = commit;
    }, [commit]);

    if (value !== committed) {
        setCommitted(value);
        setDraft(value);
    }

    useEffect(() => {
        // A pause over a draft since replaced from elsewhere is no longer the reader's text.
        if (pausedDraft === draft && pausedDraft !== committed) {
            setCommitted(pausedDraft);
            commitRef.current(pausedDraft);
        }
    }, [pausedDraft, draft, committed]);

    return [draft, setDraft] as const;
};
